import React from 'react';

interface TaskPosition {
  id: string;
  x: number;
  width: number;
  y: number;
  barHeight: number;
}

interface GanttDependencyArrowsProps {
  tasks: { id: string; dependencies: string[] }[];
  taskPositions: Map<string, TaskPosition>;
  barHeight: number;
  showCriticalPath: boolean;
  criticalPathIds?: Set<string>;
  criticalPathColor?: string;
}

export const GanttDependencyArrows: React.FC<GanttDependencyArrowsProps> = ({
  tasks,
  taskPositions,
  barHeight,
  showCriticalPath,
  criticalPathIds,
  criticalPathColor = '#EF4444',
}) => {
  // Build arrows from predecessor → dependent
  const arrows: { fromId: string; toId: string; isCritical: boolean }[] = [];
  const visited = new Set<string>();

  for (const task of tasks) {
    if (!task.dependencies || task.dependencies.length === 0) continue;
    for (const depId of task.dependencies) {
      // Skip circular dependencies
      const key = `${depId}->${task.id}`;
      const reverseKey = `${task.id}->${depId}`;
      if (visited.has(reverseKey)) continue;
      visited.add(key);

      const isCritical = showCriticalPath && criticalPathIds?.has(depId) && criticalPathIds?.has(task.id);
      arrows.push({ fromId: depId, toId: task.id, isCritical: !!isCritical });
    }
  }

  return (
    <g data-testid="gantt-dependency-arrows">
      {/* Arrowhead marker definition */}
      <defs>
        <marker
          id="arrowhead"
          markerWidth="8"
          markerHeight="6"
          refX="8"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="#9CA3AF" />
        </marker>
        <marker
          id="arrowhead-critical"
          markerWidth="8"
          markerHeight="6"
          refX="8"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill={criticalPathColor} />
        </marker>
      </defs>

      {arrows.map(({ fromId, toId, isCritical }) => {
        const from = taskPositions.get(fromId);
        const to = taskPositions.get(toId);
        if (!from || !to) return null;

        // Start point: right edge of predecessor, vertically centered
        const startX = from.x + from.width;
        const startY = from.y + from.barHeight / 2;

        // End point: left edge of dependent, vertically centered
        const endX = to.x;
        const endY = to.y + to.barHeight / 2;

        // Cubic bezier control points for smooth routing
        const midX = startX + (endX - startX) * 0.5;

        const pathD = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;

        return (
          <path
            key={`dep-${fromId}-${toId}`}
            d={pathD}
            fill="none"
            stroke={isCritical ? criticalPathColor : '#9CA3AF'}
            strokeWidth={isCritical ? 2 : 1.5}
            strokeDasharray={isCritical ? undefined : '4,2'}
            markerEnd={isCritical ? 'url(#arrowhead-critical)' : 'url(#arrowhead)'}
            opacity={0.7}
            data-testid={`gantt-dep-arrow-${fromId}-${toId}`}
          />
        );
      })}
    </g>
  );
};

GanttDependencyArrows.displayName = 'GanttDependencyArrows';
